import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { APP_CONFIG, AppConfig } from '../../../app.config';
import { Account } from '../../models/operations/account';
import { Currency } from '../../models/operations/currency';
import { Preferences } from '../../models/user/preferences';
import { User } from '../../models/user/user';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private httpClient = inject(HttpClient);
  private storageService = inject(StorageService);
  private config = inject<AppConfig>(APP_CONFIG);

  private readonly apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = `${this.config.api}/api`;
  }

  public getCurrentUser(): Observable<User> {
    return this.httpClient.get<User>(`${this.apiBaseUrl}/user`);
  }

  public getUserPreferences(): Observable<Preferences> {
    return this.httpClient.get<any>(`${this.apiBaseUrl}/preferences`).pipe(
      map((response: any) => {
        const preferences = new Preferences(
          response.user,
          response.currency as Currency,
          response.mainAccount as Account,
          response.echeanceDelay,
          response.pointEcheance,
          response.modeAutoImport,
          response.simpleMode,
          response.sortByDate,
          response.sortedCategoryIds,
          response.usageName,
          response.language
        );
        return preferences;
      })
    );
  }

  public updateUserPreferences(pref: Preferences): Observable<Preferences> {
    if (pref.user) {
      this.storageService.setUser(pref.user); 
      const currentUser = this.storageService.getUser();
      if (currentUser) {
        currentUser.usageName = pref.user.usageName; 
        if (currentUser.language !== pref.language) {
          //this.storageService.setLanguage(pref.language);
          currentUser.language = pref.language; 
        }
        this.storageService.setUser(currentUser);
      }
    }
    const requestData = {
      user: pref.user,
      currency: pref.currency,
      mainAccount: pref.mainAccount,
      echeanceDelay: pref.echeanceDelay,
      pointEcheance: pref.pointEcheance,
      modeAutoImport: pref.modeAutoImport,
      simpleMode: pref.simpleMode,
      sortByDate: pref.sortByDate,
      sortedCategoryIds: pref.sortedCategoryIds,
      usageName: pref.usageName,
      language: pref.language
    };

    return this.httpClient.post<any>(`${this.apiBaseUrl}/preferences`, requestData).pipe(
      map((response: any) => {
        const updatedPref = new Preferences(
          response.user,
          response.currency as Currency,
          response.mainAccount as Account,
          response.echeanceDelay,
          response.pointEcheance,
          response.modeAutoImport,
          response.simpleMode,
          response.sortByDate,
          response.sortedCategoryIds,
          response.usageName,
          response.language
        );
        this.storageService.setUserPreferences(updatedPref); 
        return updatedPref;
      })
    );
  }

  public saveUserPreferences(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getUserPreferences().subscribe({
        next: (response: Preferences) => {
          this.storageService.setUserPreferences(response);
          resolve();
        },
        error: (error: any) => {
          console.error("Error while setting user preferences", error);
          reject(error);
        }
      });
    });
  }

  public initSubscription(user: User | undefined): any {
    if (user) {
      const activeSubscriptions: any[] = [];
      user?.subscriptions?.forEach(function (oneSubscription) {
        if (!oneSubscription.deleted) {
          activeSubscriptions.push(oneSubscription);
        }
      });

      if (activeSubscriptions?.length > 0) {
        // Sort subscriptions by subscription date (most recent first)
        activeSubscriptions.sort((a, b) => {
          const dateA = a.subscrDate ? new Date(a.subscrDate).getTime() : 0;
          const dateB = b.subscrDate ? new Date(b.subscrDate).getTime() : 0;
          return dateB - dateA; // Descending order (most recent first)
        });

        const subscription = activeSubscriptions[0];
        const isAnnual = parseFloat(subscription?.amount || '0') > 15;
        
        if (isAnnual) {
          const subscrDate = new Date(subscription?.subscrDate);
          subscrDate.setFullYear(subscrDate.getFullYear() + 1);
          subscription.endDate = subscrDate;
          
          // Set profileEndDate for annual subscriptions
          if (user.profileEndDate === 0 || !user.profileEndDate) {
            user.profileEndDate = subscrDate.getTime();
          }
        }
        return subscription;
      }
    }
    return "";
  }

  public isDevUser(user: User | undefined): boolean {
    return user?.profile?.name === 'DEV';
  }
  public isAdmin(user: User | undefined): boolean {
    return user?.profile?.name === 'ADMIN';
  }

  public isStandardUser(user: User | undefined): boolean {
    return user?.profile?.name === 'USER' && !this.hasAutoImport(user);
  }

  public isStandardAutoImportUser(user: User | undefined): boolean {
    return user?.profile?.name === 'AUTOIMP' && this.hasAutoImport(user);
  }

  public isEssentielUser(user: User | undefined): boolean {
    return user?.profile?.name === 'ESSENTIEL';
  }

  public isEssentielGiftedUser(user: User | undefined): boolean {
    return user?.profile?.name === 'ESSENTIEL' && !this.hasSubscription(user);
  }

  public isProUser(user: User | undefined): boolean {
    return user?.profile?.name === 'PRO';
  }

  public isProGiftedUser(user: User | undefined): boolean {
    return user?.profile?.name === 'PRO' && !this.hasSubscription(user);
  }

  public isPremiumUser(user: User | undefined): boolean {
    if (user?.profile?.name !== 'PREMIUM') {
      return false;
    }
    
    // If user has active subscription, they are a regular Premium user
    if (this.hasSubscription(user)) {
      return true;
    }
    
    // Premium découverte: PREMIUM profile with profileEndDate (trial period)
    // Premium découverte has the same access as Premium, including bank synchronization
    if (user?.profileEndDate) {
      return true;
    }
    
    // If no active subscription but has PREMIUM profile with no profileEndDate,
    // check if they have any subscriptions (even deleted) - if yes, they are a paying Premium user
    // This handles cases where subscriptions are marked as deleted but user still has the profile
    if (!user?.profileEndDate && user?.subscriptions && user.subscriptions.length > 0) {
      // Check if there are any PREMIUM subscriptions (even deleted)
      const hasPremiumSubscription = user.subscriptions.some(sub => 
        (sub.type || '').toUpperCase() === 'PREMIUM'
      );
      if (hasPremiumSubscription) {
        return true;
      }
    }
    
    return false;
  }

  public isPremiumGiftedUser(user: User | undefined): boolean {
    // Premium Gifted: PREMIUM profile, no profileEndDate, and NO subscriptions at all
    return user?.profile?.name === 'PREMIUM' && 
           !user?.profileEndDate && 
           (!user?.subscriptions || user.subscriptions.length === 0);
  }

  public isPremiumDiscoveryUser(user: User | undefined): boolean {
    return user?.profile?.name === 'PREMIUM' && !!user?.profileEndDate && !this.hasSubscription(user);
  }

  public hasAutoImport(user: User | undefined): boolean {
    if (user?.subscriptions) {
      return user.subscriptions.some(oneSubscription => 
        oneSubscription.type === 'AUTO_IMPORT' && !oneSubscription.deleted
      );
    }
    return false;
  }

  public hasSubscription(user: User | undefined): boolean {
    if (user?.subscriptions) {
      return user.subscriptions.some(oneSubscription => !oneSubscription.deleted);
    }
    return false;
  }

  public hasAnnualSubscription(user: User | undefined): boolean {
    if (user?.subscriptions) {
      return user.subscriptions.some(oneSubscription => 
        !oneSubscription.deleted && parseFloat(oneSubscription.amount || '0') > 15
      );
    }
    return false;
  }

  public hasMonthlySubscription(user: User | undefined): boolean {
    if (user?.subscriptions) {
      return user.subscriptions.some(oneSubscription => 
        !oneSubscription.deleted && parseFloat(oneSubscription.amount || '0') < 15
      );
    }
    return false;
  }

  public hasUserProfile(user: User | undefined): boolean {
    return user?.profile ? ['DEV', 'USER'].includes(user.profile.name) : false;
  }

  public hasAccessTo(user: User | undefined, feature: string): boolean {
    if (user?.profile?.functionalities) {
      return user.profile.functionalities.some(functionality => feature.includes(functionality.code));
    }
    return false;
  }

  public getAccounts(): Observable<Account[]> {
    return this.httpClient.get<Account[]>(`${this.apiBaseUrl}/account`);
  }
}